/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Modal,
  ModalIcon,
  ModalProps,
  PageBody,
  PageSection,
  Snackbar,
  Spacer,
  Stack,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { IPage } from '../../types';

import { CustomAuthMethodTypeEnum } from '@pypestream/api-services';
import { TranslationComponent } from '@pypestream/translations';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../../../components';
import { OrganizationRoleNames, useAuthRole } from '../../../hooks';
import {
  sendManagerEvent,
  useManagerCtxSelector,
  useManagerStateMatches,
  useManagerStateMatchesAllOf,
} from '../../../xstate/app.xstate';
import {
  FormOrgInfoType,
  OIDC_AUTH_METHOD_DEFAULT_VALUES,
  SAML_AUTH_METHOD_DEFAULT_VALUES,
} from './create-organization';
import { OrganizationDetails } from './organization-details';

export const OrganizationSettings: FC<IPage> = () => {
  const navigate = useNavigate();
  const { routes, users, currentOrg, orgs, availableDefaultAuthMethods } =
    useManagerCtxSelector((ctx) => ({
      users: ctx.users,
      routes: ctx.routes,
      orgs: ctx.orgs,
      currentOrg: ctx.orgs?.find(({ id }) => id === ctx.selectedOrgId),
      availableDefaultAuthMethods: ctx.defaultAuthMethods,
    }));

  const parentOrg = useMemo(() => {
    if (currentOrg && currentOrg.parentId) {
      return orgs?.find(({ id }) => id === currentOrg.parentId);
    }

    return null;
  }, [currentOrg, orgs]);

  const isReadyToLoad = useManagerStateMatchesAllOf([
    'orgRelated.ready.users.idle',
    'orgRelated.ready.currentOrg.selected',
  ]);
  const isOrgUpdating = useManagerStateMatches(
    'orgRelated.ready.orgs.updating'
  );

  const isUpdatingOrg = useManagerStateMatches(
    'orgRelated.ready.orgs.updating'
  );

  const isLoadingOrg = useManagerStateMatches('orgRelated.ready.orgs.loading');

  const isDeletingOrg = useManagerStateMatches(
    'orgRelated.ready.orgs.deleting'
  );

  const hasInflightNetworkRequest =
    isUpdatingOrg || isDeletingOrg || isLoadingOrg;

  const form = useForm<FormOrgInfoType>({
    defaultValues: {
      name: '',
      picture: '',
      accountIconId: undefined,
      accountManagerId: '',
      customAuthMethods: [],
      defaultAuthMethods: [],
    },
  });

  const modalRef = useRef<ModalProps>(null);

  const isAdminRole = useAuthRole([
    OrganizationRoleNames.ADMIN,
    OrganizationRoleNames.SUPER_ADMIN,
  ]);

  const navigateToChildOrgs = useCallback(
    () => navigate(routes.orgs),
    [navigate, routes.orgs]
  );

  useEffect(() => {
    if (isReadyToLoad) {
      sendManagerEvent('manager.users.loadUsers');
    }
  }, [isReadyToLoad]);

  useEffect(() => {
    if (!currentOrg) return;
    const savedAccountAuthMethods =
      currentOrg.accountAuthMethodSettings?.map((accountAuthMethodSetting) => ({
        ...accountAuthMethodSetting,
        name:
          availableDefaultAuthMethods?.find(
            (availableDefaultAuthMethod) =>
              availableDefaultAuthMethod.id ===
              accountAuthMethodSetting.authMethodId
          )?.name || '',
        isCustomAuthMethod: !!accountAuthMethodSetting.customAuthMethodId,
        isDefault: !!accountAuthMethodSetting.isDefault,
      })) || [];

    const customAuthMethods = (currentOrg.customAuthMethods || []).map(
      (customAuthMethod) => {
        const savedAccountAuthMethod = savedAccountAuthMethods.find(
          (_savedAccountAuthMethod) =>
            _savedAccountAuthMethod.customAuthMethodId === customAuthMethod.id
        );
        return {
          ...customAuthMethod,
          accountAuthMethodSettingId: savedAccountAuthMethod?.id,
          isActive: savedAccountAuthMethod?.isActive,
        };
      }
    );

    const hasOIDCAuthMethod = customAuthMethods?.some(
      (customAuthMethod) =>
        customAuthMethod.type === CustomAuthMethodTypeEnum.Oidc
    );

    if (!hasOIDCAuthMethod) {
      customAuthMethods.push(OIDC_AUTH_METHOD_DEFAULT_VALUES);
    }

    const hasSAMLAuthMethod = customAuthMethods?.some(
      (customAuthMethod) =>
        customAuthMethod.type === CustomAuthMethodTypeEnum.Saml
    );

    if (!hasSAMLAuthMethod) {
      customAuthMethods.push(SAML_AUTH_METHOD_DEFAULT_VALUES);
    }

    const orgDetails = {
      name: currentOrg.name,
      picture: currentOrg.pictureFile?.url,
      accountManagerId: currentOrg.accountManagerId,
      defaultAuthMethods: savedAccountAuthMethods?.filter(
        (method) => !method.customAuthMethodId
      ),
      customAuthMethods,
    };

    form.reset({ ...orgDetails });
  }, [currentOrg]);

  const onSubmit = (values: FormOrgInfoType) => {
    sendManagerEvent({
      type: 'manager.updateOrg',
      name: values.name,
      accountManagerId: currentOrg?.accountManagerId,
      generalSettings: currentOrg?.settings?.general ?? {},
      resourceLimitSettings: currentOrg?.settings?.resourceLimits ?? {},
      accountIconId: values.accountIconId,
      customAuthMethods: values.customAuthMethods
        ?.filter((customAuthMethod) => customAuthMethod.isActive)
        ?.map(
          ({
            isActive,
            isDefault,
            accountAuthMethodSettingId,
            ...restFields
          }) => restFields
        ),
      accountAuthMethodSettings: [
        ...(values.defaultAuthMethods || []),
        ...(values.customAuthMethods || []),
      ]
        ?.filter((accountAuthMethodSetting) => accountAuthMethodSetting.id)
        .map((accountAuthMethodSetting) => ({
          id:
            accountAuthMethodSetting.accountAuthMethodSettingId! ||
            accountAuthMethodSetting.id!,
          isActive: !!accountAuthMethodSetting.isActive,
          isDefault: !!accountAuthMethodSetting.isDefault,
        })),
    });
  };

  const closeModal = () => {
    if (modalRef.current) {
      modalRef.current.open = false;
    }
  };

  const openModal = () => {
    if (modalRef.current) {
      modalRef.current.open = true;
    }
  };

  const deleteOrg = () => {
    if (currentOrg) {
      sendManagerEvent({
        type: 'manager.deleteOrg',
        id: currentOrg.id,
        callback: (res) => {
          if (res) {
            navigate(`/organization/${currentOrg.parentId}/child-orgs`);
          }
        },
      });
      closeModal();
    }
  };

  if (!currentOrg) {
    return (
      <PageBody background="none">
        <PageSection>
          <Loader relative />
        </PageSection>
      </PageBody>
    );
  }

  return (
    <PageBody background="none">
      <TextTitle test-id="org-details-title">{currentOrg.name}</TextTitle>
      {isLoadingOrg ? (
        <Loader relative />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Spacer size="large" />
          <OrganizationDetails
            form={form}
            parentId={currentOrg.parentId || ''}
            users={users || []}
            isAdminRole={isAdminRole}
            isEdit
            hasInflightNetworkRequest={!!hasInflightNetworkRequest}
          />
          <Snackbar
            id="cerate-org-snackbar"
            open={
              !!Object.keys(form.formState.dirtyFields).length && isAdminRole
            }
          >
            There are unsaved changes
            <Button
              variant="secondary"
              size="large"
              onClick={() => {
                form.reset();
              }}
              disabled={!!hasInflightNetworkRequest || isOrgUpdating}
              slot="action"
              test-id="cancel-org-changes-btn"
            >
              <TranslationComponent i18nKey="manager/organizations:editOrg.cancel">
                Cancel
              </TranslationComponent>
            </Button>
            <Button
              size="large"
              onClick={form.handleSubmit(onSubmit)}
              disabled={
                !Object.keys(form.formState.dirtyFields).length ||
                !!hasInflightNetworkRequest ||
                isOrgUpdating
              }
              slot="action"
              test-id="save-org-changes-btn"
            >
              {isUpdatingOrg ? (
                <TranslationComponent i18nKey="manager/organizations:editOrg.saving">
                  Saving...
                </TranslationComponent>
              ) : (
                <TranslationComponent i18nKey="manager/organizations:editOrg.save">
                  Save Changes
                </TranslationComponent>
              )}
            </Button>
          </Snackbar>
          {isAdminRole ? (
            <Stack justifyContent="space-between" alignItems="center">
              <Stack>
                {parentOrg && (
                  <Button
                    test-id="remove-org-btn"
                    variant="warning"
                    size="large"
                    onClick={openModal}
                    disabled={!!hasInflightNetworkRequest || isOrgUpdating}
                  >
                    {isDeletingOrg ? (
                      'Deleting Org...'
                    ) : (
                      <TranslationComponent i18nKey="manager/organizations:editOrg.delete">
                        Delete Org
                      </TranslationComponent>
                    )}
                  </Button>
                )}
              </Stack>
            </Stack>
          ) : (
            <Stack justifyContent="start" alignItems="center">
              <Stack>
                <Button
                  variant="secondary"
                  size="large"
                  onClick={() => {
                    navigateToChildOrgs();
                  }}
                >
                  <TranslationComponent i18nKey="manager/organizations:editOrg.back">
                    Back
                  </TranslationComponent>
                </Button>
              </Stack>
            </Stack>
          )}
        </form>
      )}
      <Modal
        test-id="remove-org-modal"
        ref={modalRef}
        size="medium"
        onClose={closeModal}
      >
        <ModalIcon
          name="error"
          slot="header"
          style={{ display: 'flex', justifyContent: 'center' }}
        />
        <Stack slot="header" alignItems="center" direction="column">
          <TextTitle size="small">
            <TranslationComponent i18nKey="manager/organizations:editOrg.deleteModal.areYouSure">
              Are you sure?
            </TranslationComponent>
          </TextTitle>
          <TextBody variant="secondary">
            <TranslationComponent i18nKey="manager/organizations:editOrg.deleteModal.description"></TranslationComponent>
          </TextBody>
        </Stack>
        <Stack slot="footer" justifyContent="end">
          <Button
            test-id="cancel-deletion-org-btn"
            variant="ghost"
            size="large"
            type="button"
            onClick={closeModal}
          >
            <TranslationComponent i18nKey="manager/organizations:editOrg.deleteModal.cancel">
              Cancel
            </TranslationComponent>
          </Button>
          <Button
            test-id="confirm-deletion-org-btn"
            size="large"
            variant="warning"
            data-cy="delete-org"
            type="button"
            onClick={deleteOrg}
            disabled={!!hasInflightNetworkRequest}
          >
            <TranslationComponent i18nKey="manager/organizations:editOrg.deleteModal.removeOrg">
              Delete Org
            </TranslationComponent>
          </Button>
        </Stack>
      </Modal>
    </PageBody>
  );
};
